import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const BookmarkedMissions = ({ bookmarkedMissions, onRemoveBookmark, onAddNote }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [editingNote, setEditingNote] = useState(null);
  const [noteText, setNoteText] = useState('');

  const filteredMissions = bookmarkedMissions?.filter(mission =>
    mission?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    mission?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  const handleSaveNote = (missionId) => {
    onAddNote(missionId, noteText);
    setEditingNote(null);
    setNoteText('');
  };

  const handleEditNote = (mission) => {
    setEditingNote(mission?.id);
    setNoteText(mission?.personalNote || '');
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy':
        return 'text-success bg-success/10';
      case 'medium':
        return 'text-warning bg-warning/10';
      case 'hard':
        return 'text-error bg-error/10';
      default:
        return 'text-muted-foreground bg-muted/10';
    }
  };

  const getCategoryIcon = (category) => {
    const iconMap = {
      'web-scraping': 'Globe',
      'form-automation': 'FileText',
      'testing': 'CheckSquare',
      'navigation': 'Navigation',
      'data-extraction': 'Database',
      'performance': 'Zap'
    };
    return iconMap?.[category] || 'Code';
  };

  return (
    <div className="bg-card rounded-lg border border-subtle p-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <h2 className="text-xl font-semibold text-foreground">Bookmarked Missions</h2>
        <div className="w-full sm:w-auto">
          <Input
            type="search"
            placeholder="Search bookmarked missions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="min-w-64"
          />
        </div>
      </div>
      <div className="space-y-4">
        {filteredMissions?.map((mission) => (
          <div
            key={mission?.id}
            className="bg-muted/30 rounded-lg p-4 hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-start gap-4">
              {/* Mission Icon */}
              <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name={getCategoryIcon(mission?.category)} size={24} color="var(--color-primary)" />
              </div>

              {/* Mission Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4 mb-2">
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">{mission?.title}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {mission?.description}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${getDifficultyColor(mission?.difficulty)}`}>
                      {mission?.difficulty}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      iconName="Bookmark"
                      onClick={() => onRemoveBookmark(mission?.id)}
                      className="text-warning hover:text-warning/80"
                    />
                  </div>
                </div>

                {/* Mission Meta */}
                <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
                  <span className="flex items-center gap-1">
                    <Icon name="Tag" size={12} />
                    {mission?.category?.replace('-', ' ')}
                  </span>
                  <span className="flex items-center gap-1">
                    <Icon name="Clock" size={12} />
                    ~{mission?.estimatedTime} min
                  </span>
                  <span className="flex items-center gap-1">
                    <Icon name="Star" size={12} />
                    {mission?.xpReward} XP
                  </span>
                  <span className="flex items-center gap-1">
                    <Icon name="Calendar" size={12} />
                    Bookmarked {new Date(mission.bookmarkedAt)?.toLocaleDateString()}
                  </span>
                </div>

                {/* Personal Note Section */}
                <div className="space-y-2">
                  {editingNote === mission?.id ? (
                    <div className="space-y-2">
                      <textarea
                        value={noteText}
                        onChange={(e) => setNoteText(e?.target?.value)}
                        placeholder="Add your personal notes about this mission..."
                        className="w-full p-3 bg-input border border-subtle rounded-lg text-sm text-foreground placeholder-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                        rows={3}
                      />
                      <div className="flex items-center gap-2">
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => handleSaveNote(mission?.id)}
                        >
                          Save Note
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditingNote(null);
                            setNoteText('');
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      {mission?.personalNote ? (
                        <div className="bg-accent/10 border border-accent/20 rounded-lg p-3">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <Icon name="StickyNote" size={16} color="var(--color-accent)" />
                            <Button
                              variant="ghost"
                              size="xs"
                              iconName="Edit"
                              onClick={() => handleEditNote(mission)}
                            >
                              Edit
                            </Button>
                          </div>
                          <p className="text-sm text-foreground">{mission?.personalNote}</p>
                        </div>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Plus"
                          iconPosition="left"
                          onClick={() => handleEditNote(mission)}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          Add personal note
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-2 mt-4">
                  <Button
                    variant="default"
                    size="sm"
                    iconName="Play"
                    iconPosition="left"
                    onClick={() => window.open(`/mission-workspace?id=${mission?.id}`, '_blank')}
                  >
                    Start Mission
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Eye"
                    iconPosition="left"
                    onClick={() => window.open(`/mission-details?id=${mission?.id}`, '_blank')}
                  >
                    View Details
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {filteredMissions?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Bookmark" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">
            {searchTerm ? 'No matching bookmarks' : 'No bookmarked missions'}
          </h3>
          <p className="text-muted-foreground mb-4">
            {searchTerm 
              ? 'Try adjusting your search terms to find what you\'re looking for.'
              : 'Bookmark missions you want to revisit later from the mission map.'
            }
          </p>
          {!searchTerm && (
            <Button
              variant="default"
              iconName="Map"
              iconPosition="left"
              onClick={() => window.open('/mission-map-dashboard', '_blank')}
            >
              Explore Missions
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default BookmarkedMissions;